/**
 * #902 — recover album-open when a wall tile carries a stale stable id.
 * Resolve by on-disk path hints, then title+artist, before surfacing an error.
 */
import { libraryService } from './libraryService';
import {
  findLocalAlbumIndexRow,
  findLocalAlbumIndexRowByPath,
  findLocalAlbumIndexRowByTitleArtist,
  getLocalAlbumIndexSnapshot,
} from './libraryAlbumIndex';
import { normalizeLocalAlbumId } from './localLibraryMetadata';
import { isAlbumNotFoundError } from '../utils/libraryMaintenanceErrors';

export { isAlbumNotFoundError };

export type StaleAlbumOpenHints = {
  library_folder_path?: string | null;
  sample_file_path?: string | null;
  title?: string | null;
  artist?: string | null;
  artist_name?: string | null;
};

function canonicalKeyFromRow(row: Record<string, unknown> | null | undefined): string | null {
  if (!row) return null;
  const raw = String(row.album_key ?? row.id ?? '').trim();
  return raw ? normalizeLocalAlbumId(raw) : null;
}

/** Resolve a surviving catalog row for a stale tile id using path/title hints. */
export function resolveStaleAlbumCatalogKey(
  staleKey: string,
  hints: StaleAlbumOpenHints = {},
): string | null {
  const normalizedStale = normalizeLocalAlbumId(String(staleKey || '').trim());
  if (!normalizedStale) return null;

  const existing = findLocalAlbumIndexRow(normalizedStale);
  if (existing) return canonicalKeyFromRow(existing);

  const folderPath = hints.library_folder_path ?? null;
  const samplePath = hints.sample_file_path ?? null;
  const byPath = findLocalAlbumIndexRowByPath(folderPath, samplePath);
  if (byPath) {
    const key = canonicalKeyFromRow(byPath);
    if (key && key !== normalizedStale) return key;
  }

  const title = hints.title ?? null;
  const artist = hints.artist ?? hints.artist_name ?? null;
  const byTitle = findLocalAlbumIndexRowByTitleArtist(title, artist);
  if (byTitle) {
    const key = canonicalKeyFromRow(byTitle);
    if (key && key !== normalizedStale) return key;
  }

  const snap = getLocalAlbumIndexSnapshot();
  for (const row of snap.albums || []) {
    const key = canonicalKeyFromRow(row as Record<string, unknown>);
    if (!key || key === normalizedStale) continue;
    const rowFolder = String((row as Record<string, unknown>).library_folder_path ?? '').trim();
    const rowSample = String((row as Record<string, unknown>).sample_file_path ?? '').trim();
    if (folderPath && rowFolder && rowFolder === folderPath) return key;
    if (samplePath && rowSample && rowSample === samplePath) return key;
  }

  return null;
}

/** Fetch album detail using stale-id recovery hints — null when unrecoverable. */
export async function fetchLocalAlbumDetailWithStaleRecovery(
  albumKey: string,
  hints: StaleAlbumOpenHints = {},
): Promise<Awaited<ReturnType<typeof libraryService.getAlbum>> | null> {
  const normalized = normalizeLocalAlbumId(albumKey);
  try {
    const direct = await libraryService.getAlbum(normalized);
    if (direct?.success) return direct;
  } catch (error) {
    if (!isAlbumNotFoundError(error)) throw error;
  }

  const recoveredKey = resolveStaleAlbumCatalogKey(normalized, hints);
  if (!recoveredKey || recoveredKey === normalized) return null;

  try {
    const recovered = await libraryService.getAlbum(recoveredKey);
    if (recovered?.success) return recovered;
  } catch (error) {
    if (!isAlbumNotFoundError(error)) throw error;
  }
  return null;
}
